import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { videoPlans } from '../api';
import { SECTIONS, STATUSES, TARGETS, estimateSeconds, formatSeconds, wordCount } from './videoPlanSections';
import './TeachingsHub.css';

const statusOf = (key) => STATUSES.find(s => s.key === key) || STATUSES[0];

// ───────── one beat of the video ─────────
const Beat = ({ item, canUp, canDown, onSave, onMove, onSection, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setText(item.text); }, [item.text, editing]);

  const save = async () => {
    const next = text.trim();
    if (!next || next === item.text) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(item.id, { text: next }); setEditing(false); } catch (e) { /* the screen shows the error */ } finally { setSaving(false); }
  };

  return (
    <div className={`vp-beat${editing ? ' is-editing' : ''}`}>
      <div className="vp-beat-top">
        <span className="vp-beat-kind">{item.type === 'teaching' ? '🎓 Teaching' : '📝 Note'}{item.label ? ` · ${item.label}` : ''}</span>
        <span className="vp-beat-words">{wordCount(item.text)} words</span>
      </div>

      {editing ? (
        <>
          <textarea className="vp-textarea" value={text} onChange={(e) => setText(e.target.value)} rows={5} autoFocus aria-label="Edit this beat" />
          <div className="vp-beat-actions">
            <button type="button" className="th-primary" onClick={save} disabled={saving || !text.trim()}>{saving ? 'Saving…' : 'Save'}</button>
            <button type="button" className="th-ghost" onClick={() => { setText(item.text); setEditing(false); }} disabled={saving}>Cancel</button>
          </div>
        </>
      ) : (
        <button type="button" className="vp-beat-text" onClick={() => setEditing(true)} aria-label="Tap to read and edit">{item.text}</button>
      )}

      {!editing && (
        <div className="vp-beat-tools">
          <button type="button" className="vp-icon" onClick={() => onMove(item.id, -1)} disabled={!canUp} aria-label="Move up">▲</button>
          <button type="button" className="vp-icon" onClick={() => onMove(item.id, 1)} disabled={!canDown} aria-label="Move down">▼</button>
          <select className="vp-select" value={item.section} onChange={(e) => onSection(item.id, e.target.value)} aria-label="Section">
            {SECTIONS.map(s => <option key={s.key} value={s.key}>{s.emoji} {s.short}</option>)}
          </select>
          <span className="vp-grow" />
          <button type="button" className="vp-icon vp-edit" onClick={() => setEditing(true)} aria-label="Edit">✏️</button>
          {confirmDelete ? (
            <>
              <button type="button" className="vp-danger" onClick={() => onDelete(item.id)}>Delete</button>
              <button type="button" className="th-ghost" onClick={() => setConfirmDelete(false)}>Keep</button>
            </>
          ) : (
            <button type="button" className="vp-icon" onClick={() => setConfirmDelete(true)} aria-label="Delete this beat">🗑️</button>
          )}
        </div>
      )}
    </div>
  );
};

// ───────── add a free note to a section ─────────
const NoteAdder = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) return <button type="button" className="vp-add" onClick={() => setOpen(true)}>+ Add a line</button>;
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try { await onAdd(text.trim()); setText(''); setOpen(false); } catch (e) { /* the screen shows the error */ } finally { setBusy(false); }
  };
  return (
    <form className="vp-note-form" onSubmit={submit}>
      <textarea className="vp-textarea" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Write what you will say or show…" autoFocus aria-label="New line" />
      <div className="vp-beat-actions">
        <button type="submit" className="th-primary" disabled={busy || !text.trim()}>Add</button>
        <button type="button" className="th-ghost" onClick={() => { setText(''); setOpen(false); }}>Cancel</button>
      </div>
    </form>
  );
};

// ───────── the plan screen ─────────
const PlanDetail = ({ planId, onBack, onChanged }) => {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [script, setScript] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmPlanDelete, setConfirmPlanDelete] = useState(false);

  const apply = useCallback((p) => { setPlan(p); setTitleDraft(p.title); setNotesDraft(p.notes || ''); onChanged(); }, [onChanged]);

  useEffect(() => {
    let alive = true;
    videoPlans.get(planId)
      .then(res => { if (alive) { setPlan(res.data.plan); setTitleDraft(res.data.plan.title); setNotesDraft(res.data.plan.notes || ''); } })
      .catch(() => { if (alive) setError('Could not open this video plan.'); });
    return () => { alive = false; };
  }, [planId]);

  const run = async (fn) => {
    setError('');
    try { const res = await fn(); apply(res.data.plan); } catch (err) { setError(err.response?.data?.error || 'That did not save. Please try again.'); throw err; }
  };

  const items = plan ? plan.items : [];
  const seconds = useMemo(() => estimateSeconds(items), [items]);

  if (!plan) {
    return (
      <div className="vp-detail">
        <button type="button" className="vp-back" onClick={onBack}>← All videos</button>
        {error ? <div className="th-error">{error}</div> : <div className="th-muted">Loading…</div>}
      </div>
    );
  }

  const pct = Math.min(100, Math.round((seconds / plan.targetSeconds) * 100));
  const meterClass = seconds <= plan.targetSeconds ? 'ok' : seconds <= plan.targetSeconds * 1.15 ? 'warn' : 'over';

  const move = (id, dir) => {
    const idx = items.findIndex(i => i.id === id);
    const section = items[idx].section;
    let j = idx + dir;
    while (j >= 0 && j < items.length && items[j].section !== section) j += dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setPlan({ ...plan, items: next }); // instant on screen, then saved
    run(() => videoPlans.reorder(plan.id, next.map(i => ({ id: i.id, section: i.section })))).catch(() => {});
  };

  const scriptText = SECTIONS
    .map(s => ({ s, rows: items.filter(i => i.section === s.key) }))
    .filter(g => g.rows.length)
    .map(g => `${g.s.title.toUpperCase()}\n${g.rows.map(r => r.text).join('\n\n')}`)
    .join('\n\n— — —\n\n');

  const copy = async () => {
    try { await navigator.clipboard.writeText(`${plan.title}\n\n${scriptText}`); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { setError('Copy is not available here - select the text and copy it.'); }
  };

  const remove = async () => { try { await videoPlans.remove(plan.id); onChanged(); onBack(); } catch (err) { setError('Could not delete this plan.'); } };

  return (
    <div className="vp-detail">
      <button type="button" className="vp-back" onClick={onBack}>← All videos</button>

      <input
        className="vp-title-input"
        value={titleDraft}
        onChange={(e) => setTitleDraft(e.target.value)}
        onBlur={() => { const t = titleDraft.trim(); if (t && t !== plan.title) run(() => videoPlans.update(plan.id, { title: t })).catch(() => {}); else setTitleDraft(plan.title); }}
        aria-label="Video title"
        maxLength={120}
      />
      {plan.skillCategory && <div className="vp-skill">🎯 {plan.skillCategory}</div>}

      <div className="th-chips vp-status">
        {STATUSES.map(s => (
          <button key={s.key} type="button" className={`th-chip${plan.status === s.key ? ' is-active' : ''}`} onClick={() => run(() => videoPlans.update(plan.id, { status: s.key })).catch(() => {})}>{s.emoji} {s.label}</button>
        ))}
      </div>

      <div className="vp-meter">
        <div className="vp-meter-row">
          <span>≈ <strong>{formatSeconds(seconds)}</strong> spoken</span>
          <label className="vp-target">Target
            <select className="vp-select" value={plan.targetSeconds} onChange={(e) => run(() => videoPlans.update(plan.id, { targetSeconds: Number(e.target.value) })).catch(() => {})}>
              {[...new Set([...TARGETS, plan.targetSeconds])].sort((a, b) => a - b).map(t => <option key={t} value={t}>{formatSeconds(t)}</option>)}
            </select>
          </label>
        </div>
        <div className="vp-meter-bar"><div className={`vp-meter-fill ${meterClass}`} style={{ width: `${pct}%` }} /></div>
        {meterClass === 'over' && <div className="vp-meter-hint">About {formatSeconds(seconds - plan.targetSeconds)} too long - trim a line or two.</div>}
      </div>

      <div className="vp-viewtoggle">
        <button type="button" className={`th-seg-btn${!script ? ' is-active' : ''}`} onClick={() => setScript(false)}>Arrange</button>
        <button type="button" className={`th-seg-btn${script ? ' is-active' : ''}`} onClick={() => setScript(true)}>Script</button>
      </div>

      {error && <div className="th-error" role="alert">{error}</div>}

      {script ? (
        <div className="vp-script">
          {scriptText ? <pre className="vp-script-text">{scriptText}</pre> : <div className="th-muted">Nothing here yet. Add teachings from the Library, or add your own lines.</div>}
          {scriptText && <button type="button" className="th-primary" onClick={copy}>{copied ? 'Copied ✓' : 'Copy script'}</button>}
        </div>
      ) : (
        SECTIONS.map(s => {
          const rows = items.filter(i => i.section === s.key);
          return (
            <section key={s.key} className="vp-section">
              <h4 className="vp-section-title">{s.emoji} {s.title}<span className="vp-section-hint">{s.hint}</span></h4>
              {rows.map((it, k) => (
                <Beat
                  key={it.id}
                  item={it}
                  canUp={k > 0}
                  canDown={k < rows.length - 1}
                  onSave={(id, changes) => run(() => videoPlans.updateItem(plan.id, id, changes))}
                  onMove={move}
                  onSection={(id, section) => run(() => videoPlans.updateItem(plan.id, id, { section })).catch(() => {})}
                  onDelete={(id) => run(() => videoPlans.removeItem(plan.id, id)).catch(() => {})}
                />
              ))}
              <NoteAdder onAdd={(text) => run(() => videoPlans.addItem(plan.id, { text, section: s.key }))} />
            </section>
          );
        })
      )}

      <label className="th-label" htmlFor="vp-notes">Notes for yourself (not part of the script)</label>
      <textarea
        id="vp-notes"
        className="vp-textarea"
        rows={3}
        value={notesDraft}
        onChange={(e) => setNotesDraft(e.target.value)}
        onBlur={() => { if (notesDraft !== (plan.notes || '')) run(() => videoPlans.update(plan.id, { notes: notesDraft })).catch(() => {}); }}
        placeholder="Location, props, music, caption ideas…"
      />

      <div className="vp-dangerzone">
        {confirmPlanDelete ? (
          <>
            <span>Delete this video plan? Your teachings stay in the Library.</span>
            <button type="button" className="vp-danger" onClick={remove}>Delete plan</button>
            <button type="button" className="th-ghost" onClick={() => setConfirmPlanDelete(false)}>Keep it</button>
          </>
        ) : (
          <button type="button" className="th-ghost vp-delete-plan" onClick={() => setConfirmPlanDelete(true)}>🗑️ Delete this plan</button>
        )}
      </div>
    </div>
  );
};

// ───────── the list of plans ─────────
const VideoPlansView = ({ reloadKey = 0 }) => {
  const [plans, setPlans] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    videoPlans.list()
      .then(res => setPlans(res.data.plans || []))
      .catch(() => { setPlans([]); setError('Could not load your video plans.'); });
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const create = async (e) => {
    e.preventDefault();
    const name = title.trim();
    if (!name || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await videoPlans.create(name);
      setTitle('');
      load();
      setOpenId(res.data.plan.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the plan.');
    } finally { setBusy(false); }
  };

  if (openId) return <PlanDetail planId={openId} onBack={() => { setOpenId(null); load(); }} onChanged={load} />;

  return (
    <div className="vp-list">
      <form className="th-new vp-create" onSubmit={create}>
        <input className="th-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New video idea, e.g. “Master the lob”" maxLength={120} aria-label="New video idea" disabled={busy} />
        <button type="submit" className="th-primary" disabled={busy || !title.trim()}>+ New</button>
      </form>
      {error && <div className="th-error" role="alert">{error}</div>}

      {plans === null && <div className="th-muted">Loading…</div>}
      {plans && plans.length === 0 && !error && (
        <div className="vp-empty">
          <div className="vp-empty-icon" aria-hidden="true">🎬</div>
          <div className="vp-empty-title">No video ideas yet</div>
          <div className="vp-empty-sub">Start one above, or tap “➕ Add to a video” on any teaching in the Library.</div>
        </div>
      )}
      {plans && plans.map(p => {
        const st = statusOf(p.status);
        const over = p.estimatedSeconds > p.targetSeconds * 1.15;
        return (
          <button key={p.id} type="button" className="vp-card" onClick={() => setOpenId(p.id)}>
            <span className="vp-card-top">
              <span className="vp-card-title">{p.title}</span>
              <span className={`vp-status-pill s-${p.status}`}>{st.emoji} {st.label}</span>
            </span>
            {p.skillCategory && <span className="vp-card-skill">🎯 {p.skillCategory}</span>}
            <span className="vp-card-meta">
              {p.itemCount} {p.itemCount === 1 ? 'beat' : 'beats'} · ≈ {formatSeconds(p.estimatedSeconds)} of {formatSeconds(p.targetSeconds)}{over ? ' · too long' : ''}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default VideoPlansView;
